import BaseAutocomplete from 'react-google-autocomplete';
import { useTranslation } from 'react-i18next';
import { ErrorMessage } from '../ui/Error';
import { Label } from '../ui/Label';

export type GeocodedAddress = {
  locality?: string;
  country?: string;
  route?: string;
  lat: number | null;
  lng: number | null;
  formatted_address?: string;
  [key: string]: unknown;
};

type Props = {
  onGeocoded: (result: GeocodedAddress) => void;
  label?: string;
  error?: string;
};

function parseAddressComponents(
  addressComponents: google.maps.places.PlaceResult['address_components']
) {
  return addressComponents?.reduce((obj: { [k: string]: string }, part) => {
    const type = part.types[0];
    return {
      ...obj,
      [type]:
        part[
          type === 'administrative_area_level_1' ? 'short_name' : 'long_name'
        ],
    };
  }, {});
}

export function parsePlainAddress(address: string): GeocodedAddress {
  // Simple regex-based parsing for plain text addresses
  // This is a fallback parser for when Google Places API is not available

  const result: GeocodedAddress = {
    lat: null,
    lng: null,
  };

  // Remove any trailing/leading whitespace
  const cleanAddress = address.trim();

  // Try to extract country (assume Australia for now based on tests)
  if (cleanAddress.toLowerCase().includes('australia')) {
    result.country = 'Australia';
  }

  // Try to extract locality - look for patterns like "NSW 2042" or "Newtown NSW 2042"
  const localityRegex = /(\w+)\s+(NSW|QLD|VIC|WA|SA|TAS|NT|ACT)\s+\d{4}/i;
  const localityMatch = cleanAddress.match(localityRegex);
  if (localityMatch) {
    result.locality = localityMatch[1];
  }

  // Extract route/street - everything before the locality pattern
  const routeMatch = cleanAddress.match(
    /^(.+?)(?:\s*,?\s*\w+\s+(NSW|QLD|VIC|WA|SA|TAS|NT|ACT)\s+\d{4}.*)?$/i
  );
  if (routeMatch && routeMatch[1]) {
    result.route = routeMatch[1].trim();
  }

  return result;
}

export function parsePlaceResult(
  place: google.maps.places.PlaceResult
): GeocodedAddress {
  return {
    ...parseAddressComponents(place.address_components),
    lat: place.geometry?.location?.lat() ?? null,
    lng: place.geometry?.location?.lng() ?? null,
    formatted_address: place.formatted_address,
  };
}

export const Autocomplete = ({ onGeocoded, label, error }: Props) => {
  const { t } = useTranslation();
  return (
    <>
      <Label>{label || t('address')}</Label>
      <BaseAutocomplete
        className='input mb-5 mt-0.5 w-full rounded border border-grey-20 px-3 py-2.5 focus-visible:border-grey-40 focus-visible:outline-none'
        apiKey={import.meta.env.VITE_GOOGLE_MAPS_KEY}
        options={{
          types: ['address'],
          fields: [
            'address_components',
            'geometry.location',
            'place_id',
            'formatted_address',
          ],
          componentRestrictions: { country: 'au' },
        }}
        onPlaceSelected={(place) =>
          place && onGeocoded(parsePlaceResult(place))
        }
      />
      {error && <ErrorMessage message={error} />}
    </>
  );
};
